import {
    AUTH_PATH,
    IIS_DEV_COOKIE,
    IIS_DEV_DOMAIN_HOST,
    IIS_HOTFIX_COOKIE,
    IIS_HOTFIX_DOMAIN_HOST,
    IIS_PERF_COOKIE,
    IIS_PERF_DOMAIN_HOST,
    IIS_PRODUCTION_COOKIE,
    IIS_PROD_DOMAIN_HOST,
    IIS_QA_COOKIE,
    IIS_QA_DOMAIN_HOST,
    IIS_SESSION_STORAGE_KEY,
    IIS_STAGE_COOKIE,
    IIS_STAGE_DOMAIN_HOST,
    LOGOUT_PATH,
    PARAM_LOGGED_OUT,
    NEW_SESSION_TRACKED_ANALYTICS,
    PROFILE_PATH,
    SESSION_STORAGE_TTL,
    SETTINGS_PATH,
    USER_PATH,
    USE_SESSION_STORAGE,
    LOGIN_METHODS_PATH,
    PARAM_SIGN_UP,
    PARAM_PROGRESSIVE,
    UPDATE_PROFILE_PATH,
    LOGIN_V2_PATH,
    VIOLATION_PATH,
    REMEDIATE_PATH,
    EDO_STATUS_PATH,
} from './constants';
import { TBIDVals, UpdateUserProfileRequest, RequestParams, LoginRequestV2, PolicyViolation } from './types';

const sfdcBase = (window as any).sfdcBase || {};

export const getEnv = (sfdcBase: any): string => {
    let env = sfdcBase.env ? sfdcBase.env : 'DEV';
    if ('QA' === env) {
        env = window.location.hostname.startsWith('www-dev') ? 'DEV' : env;
    } else if ('UAT' === env) {
        env = window.location.hostname.startsWith('www-hfx') ? 'HOTFIX' : env;
    }
    return env;
};

// Setting a basic regex for XSS
const baseXSSRegex = '</|[<>()]';
const APP_ENV: string = getEnv(sfdcBase);
let iisDisabledFlag = false;

const iisSessionCookies = {
    dev: IIS_DEV_COOKIE,
    qa: IIS_QA_COOKIE,
    uat: IIS_STAGE_COOKIE,
    stage: IIS_STAGE_COOKIE,
    perf: IIS_PERF_COOKIE,
    prod: IIS_PRODUCTION_COOKIE,
    hotfix: IIS_HOTFIX_COOKIE,
};

const iisDomainHosts = {
    dev: IIS_DEV_DOMAIN_HOST,
    qa: IIS_QA_DOMAIN_HOST,
    perf: IIS_PERF_DOMAIN_HOST,
    uat: IIS_STAGE_DOMAIN_HOST,
    stage: IIS_STAGE_DOMAIN_HOST,
    prod: IIS_PROD_DOMAIN_HOST,
    hotfix: IIS_HOTFIX_DOMAIN_HOST,
};

const wwwDomains = {
    dev: 'www-dev.salesforce.com',
    qa: 'www-qa1.salesforce.com',
    perf: 'www-perf.salesforce.com',
    uat: 'www-uat1.salesforce.com',
    stage: 'www-uat1.salesforce.com',
    prod: 'www.salesforce.com',
    hotfix: 'www-hfx.salesforce.com',
};

const windowURL = new URL(window.location.href);

// check to see if iis_*t cookie exists or tbid info exists to determine if user is authenticated
export const isUserLoggedIn = async (): Promise<boolean> => {
    return !!(getIISLoggedInCookie() || (!checkSalesforceDomain() && (await fetchTBIDInfo())));
};

/**
 * Returns the iis domain host per evironment
 * @returns string domain host
 */
export const getIISDomainHost = (): string => {
    return iisDomainHosts[APP_ENV.toLowerCase()] ?? iisDomainHosts['qa'];
};

/**
 * Returns the profile domain host per evironment
 * @returns string domain host
 */
export const getWWWDomain = (): string => {
    return wwwDomains[APP_ENV.toLowerCase()] ?? wwwDomains['qa'];
};

/**
 * Checks if window is on salesforce domain
 * @returns boolean
 */
export const checkSalesforceDomain = (): boolean => {
    return window.location.hostname.endsWith('salesforce.com');
};

export const checkLoggedOutParam = (url: URL): boolean => {
    return url.searchParams.get(PARAM_LOGGED_OUT) === 'true' ? true : false;
};

//This function will if PARAM_SIGN_UP is present and PARAM_PROGRESSIVE is not equal to 'true' to emit events
export const isNewTBIDSession = (url: URL): boolean => {
    const signUpParam = url.searchParams.get(PARAM_SIGN_UP);
    const progressiveParam = url.searchParams.get(PARAM_PROGRESSIVE);
    return !!signUpParam && progressiveParam !== 'true';
};

export const deleteSearchParams = (url: URL, params: string[]) => {
    params.forEach((param) => url.searchParams.delete(param));
    //Replacing the state to prevent user from clicking the back button to retain the params and re-trigger the event
    window.history.replaceState({}, '', url.toString());
};

export const removeNewSessionAnalyticsTracker = (): void => {
    window.sessionStorage.removeItem(NEW_SESSION_TRACKED_ANALYTICS);
};

export const getUserProfile = async (): Promise<TBIDVals | undefined> => {
    return await getUserProfileTestable(windowURL);
};

/**
 * Returns the tbid info from session storage or the /user endpoint based on the url's query parameters
 * @returns
 */
export const getUserProfileTestable = async (currentWindowUrl: URL): Promise<TBIDVals | undefined> => {
    if (checkLoggedOutParam(currentWindowUrl) || !(await isUserLoggedIn())) {
        if (USE_SESSION_STORAGE) {
            removeTbidSessionStorage();
        }
        if (checkLoggedOutParam(currentWindowUrl)) {
            deleteSearchParams(currentWindowUrl, [PARAM_LOGGED_OUT]);
            removeNewSessionAnalyticsTracker();
        }
        return;
    }
    // retrieve session storage data if it exists
    let tbidSessionStorage = getTbidSessionStorage();

    if (isNewTBIDSession(currentWindowUrl)) {
        // Refresh session when user lands in homepage from a login initiated from domains outside 'iis.js'.  Also remove any login trackers.
        tbidSessionStorage = undefined;
        removeNewSessionAnalyticsTracker();
    }

    // if session storage exists and it is not expired (within 30 minute period), return session storage. Otherwise, fetch from /user
    if (USE_SESSION_STORAGE && tbidSessionStorage) {
        const currentTime = new Date().getTime();
        const tbidObj: TBIDVals = JSON.parse(tbidSessionStorage);
        if (currentTime - tbidObj.timeSetAt < SESSION_STORAGE_TTL) return tbidObj;
    }

    // fetch tbid info from /user if session storage does not exist
    const tbidResponse = await fetchTBIDInfo().catch(() => {
        console.error('Error with /user fetch');
        removeTbidSessionStorage();
        return undefined;
    });

    return tbidResponse;
};

const formatTBIDInfo = (obj: any): TBIDVals | undefined => {
    if (!obj) {
        return undefined;
    }

    return {
        timeSetAt: new Date().getTime(),
        ...obj,
    };
};

export const fetchTBIDInfo = async (): Promise<any | string> => {
    if (iisDisabledFlag) {
        return undefined;
    }

    const userEndpoint = `${getIISDomainHost()}${USER_PATH}`;
    const response = await fetch(userEndpoint, { credentials: 'include' });
    if (response.status !== 200) {
        return undefined;
    }
    // if 200, return json response
    const json = await response.json();
    const formattedData = formatTBIDInfo(json);

    // set new tbid info in session storage if enabled
    if (USE_SESSION_STORAGE && formattedData) {
        setTbidSessionStorage(JSON.stringify(formattedData));
    }
    return formattedData;
};

export const fetchLoginMethodsByClientId = async (clientId?: string): Promise<string[] | undefined> => {
    let loginMethodsEndpoint = `${getIISDomainHost()}${LOGIN_METHODS_PATH}`;
    if (clientId) {
        loginMethodsEndpoint += `?clientId=${encodeURIComponent(clientId)}`;
    }

    const response = await fetch(loginMethodsEndpoint);

    if (response.status !== 200) {
        // Throw an error for a non-200 response
        throw new Error(`Failed to fetch: ${response.statusText}`);
    }
    // If 200, return idps from json response
    return await response.json();
};

/**
 *
 * @returns JSON string of session storage
 */
export const getTbidSessionStorage = (): string | null => {
    return window.sessionStorage.getItem(IIS_SESSION_STORAGE_KEY);
};

/**
 *
 * @param tbidData - sets JSON string in session storage
 */
const setTbidSessionStorage = (tbidData: string): void => {
    window.sessionStorage.setItem(IIS_SESSION_STORAGE_KEY, tbidData);
};

/**
 * Deletes tbid_data from session storage
 */
export const removeTbidSessionStorage = (): void => {
    window.sessionStorage.removeItem(IIS_SESSION_STORAGE_KEY);
};

/**
 * Get value of iis_*t cookie
 * @returns boolean if iis_*t cookie exists
 */
export const getIISLoggedInCookie = (): boolean => {
    const iisCookie = iisSessionCookies[APP_ENV.toLowerCase()];
    const c = document.cookie;
    // Finding cookie in cookies
    const n = `${iisCookie}=`,
        i = c.indexOf(n);
    return i > -1;
};

/**
 * This fctn removes the iis_*t cookies from document.cookie
 * Does NOT remove iis_* session cookie since that cookie is HttpOnly and can't be ready via JS
 */
export const removeCookiesforDisabledIIS = (): void => {
    // remove session storage if iis-disabled is loaded
    window.sessionStorage.removeItem(IIS_SESSION_STORAGE_KEY);

    // remove iis_*t. Cannot remove iis_* cookie since it is HttpOnly
    const cookieName = iisSessionCookies[APP_ENV.toLowerCase()];
    const removeCookieStr = `${cookieName}=; path=/; domain=.salesforce.com; secure=true; expires=Thu, 01-Jan-1970 00:00:01 GMT;`;
    document.cookie = removeCookieStr;

    // set iisDisabledFlag
    iisDisabledFlag = true;
};

/**
 * Gets Login URL based on the env
 * @returns Absolute IIS path with auth
 */
export const getAuthLoginUrl = async (): Promise<string> => {
    return `${getIISDomainHost()}${AUTH_PATH}`;
};

/**
 * Gets Logout URL based on the env
 * @returns Absolute IIS path with login
 */
export const getAuthLogoutUrl = async (): Promise<string> => {
    return `${getIISDomainHost()}${LOGOUT_PATH}`;
};

/**
 * Gets profile URL based on the env
 */
export const getProfileUrl = async (): Promise<string> => {
    return `https://${getWWWDomain()}${PROFILE_PATH}`;
};

/**
 * Gets settings URL based on the env
 */
export const getSettingsUrl = async (): Promise<string> => {
    return `https://${getWWWDomain()}${SETTINGS_PATH}`;
};

// This function is ported over from c360 clientlibs.
// Source: https://github.com/sfdc-www-emu/digital-c360-client-libs/blob/8702f1d48a9adba9a6a3b8d1b13a3396707cf880/packages/common/src/utils.ts#L60
/**
 * getParam - Returns the param value if param exists. Else returns empty
 * @function getParam
 * @memberof module:page-utils
 * @param {String} p - param to get
 * @param {String} s - pass url, path, or par
 * @return {String}
 */
export const getParam = (p: string, s = location.search): string => {
    const queryIndex = s.indexOf('?');
    // Do not pass go if no params exists
    if (!p || queryIndex < 0) return '';
    // finding param for case insensitivity
    const m = s.match(new RegExp(`${p}=`, 'i'));
    // Add cased param to arg to retrieve
    p = m ? m.toString().replace('=', '') : p;
    // Parsing current URL params -- Insure only params are passed
    const params = new URLSearchParams(s.substring(queryIndex, s.length)),
        val = params.get(p) || '';
    // Sanitize will return empty string if no params exists
    return sanitize.param(val);
};

// This function is ported over from c360 clientlibs
// Source: https://github.com/sfdc-www-emu/digital-c360-client-libs/blob/8702f1d48a9adba9a6a3b8d1b13a3396707cf880/packages/common/src/utils.ts#L18
/**
 * Creating a sanitize object to support different sanitizing methods that may be needed
 * @var sanitize
 * @memberof module:page-utils
 */
export const sanitize = {
    /**
     * param - Sanitizes query params XSS
     * @var sanitize
     * @param {String} str
     * @returns {String}
     */
    param: (str: string): string => (str ? str.trim().replace(new RegExp(`${baseXSSRegex}|(%3(c|e))`, 'gi'), '') : ''),
    path: (str: string): string =>
        str
            ? encodeURI(
                  decodeURIComponent(str)
                      .trim()
                      .replace(new RegExp(`${baseXSSRegex}|'|"|;|=`, 'gi'), '')
              )
            : '',
};

// This function is ported over from c360 clientlibs
// Source: https://github.com/sfdc-www/digital-c360-client-libs/blob/8702f1d48a9adba9a6a3b8d1b13a3396707cf880/packages/common/src/utils.ts#L44
/**
 * triggerEvent - to trigger a custom event on the element
 * @function setLocalStorage
 * @memberof module:page-utils
 * @param {HTMLElement} el
 * @param {String} name
 * @param {Object} data
 * @returns {Promise<void>}
 */
export const triggerEvent = async (el: HTMLElement, name: string, data?: { [k: string]: any }): Promise<void> => {
    el.dispatchEvent(
        new CustomEvent<{ [k: string]: any }>(name, {
            bubbles: true, // Enable event bubbling
            detail: data ?? {},
        })
    );
};

export const updateProfile = async (userProfileData: UpdateUserProfileRequest) => {
    const patchProfileEndPoint = `${getIISDomainHost()}${UPDATE_PROFILE_PATH}?userProfilePayloadVersion=v0`;
    const options = {
        body: JSON.stringify(userProfileData),
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        method: 'PATCH',
    } as RequestInit;
    const response = await fetch(patchProfileEndPoint, options);
    if (response.ok) {
        const userInfoV0 = await response.json();
        const sessionData = getTbidSessionStorage();
        if (sessionData) {
            const parsedSessionData = JSON.parse(sessionData);
            const updatedSessionData: TBIDVals = {
                ...parsedSessionData,
                userInfo: userInfoV0,
                timeSetAt: new Date().getTime(),
            };
            if (USE_SESSION_STORAGE) {
                setTbidSessionStorage(JSON.stringify(updatedSessionData));
            }
        }
        return;
    } else {
        return response.text().then((text) => {
            throw new Error(`Failed to update profile: ${text}`);
        });
    }
};
/**
 * Makes an API call to v2/login
 */
export const loginV2 = async (loginReq: LoginRequestV2, headers?) => {
    const clientId = loginReq[RequestParams.CLIENT_ID];
    let loginV2Endpoint = `https://${getIISDomainHost()}${LOGIN_V2_PATH}`;
    if (clientId) {
        loginV2Endpoint += `?client_id=${encodeURIComponent(clientId)}`;
    }
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        body: JSON.stringify(loginReq),
    };
    const response = await fetch(loginV2Endpoint, options);
    if (!response.ok) {
        throw new Error(`Failed to login: ${response.statusText}`);
    }
    // If 201, return idps from json response
    return response.json();
};

export const getPolicyViolations = async (
    clientId: string,
    eventCode: string,
    headers?
): Promise<PolicyViolation[]> => {
    const iisViolationsUrl = `${getIISDomainHost()}${VIOLATION_PATH}?clientId=${clientId}&eventCode=${eventCode}`;
    const response = await fetch(iisViolationsUrl, {
        headers: {
            ...headers,
        },
        credentials: 'include',
    });

    if (response.ok) {
        return await response.json();
    }

    throw new Error(
        JSON.stringify({
            statusCode: response.status,
        })
    );
};

export const createViolationsPayload = (violations: string[], eventCode: string) => {
    return violations.map((violation) => ({
        type: violation,
        eventCode: eventCode,
    }));
};

export const remediatePolicyViolation = async (
    violations: string[],
    eventCode: string,
    clientId?: string
): Promise<void> => {
    const iisRemediationUrl = `${getIISDomainHost()}${REMEDIATE_PATH}`;

    const violationsPayload = violations.map((violation) => ({
        type: violation,
        eventCode,
        ...(clientId && { clientId }),
    }));

    const res = await fetch(iisRemediationUrl, {
        body: JSON.stringify({
            violations: violationsPayload,
        }),
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        method: 'POST',
    });

    if (!res.ok) {
        throw new Error(
            JSON.stringify({
                statusCode: res.status,
            })
        );
    }
};

export const hasEdoAccount = async (): Promise<boolean> => {
    try {
        const response = await fetch(`${getIISDomainHost()}${EDO_STATUS_PATH}`, {
            credentials: 'include',
        });
        return response.status === 200;
    } catch (error) {
        console.error('Error checking edo status: ', error);
        return false;
    }
};
